import { ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useRevealWebhookSecretMutation } from "@/pages/Private/redux/webhook/webhook.api";
import { showGlobalToast } from "@/utils/showGlobalToast";
import { ERROR_TYPE, normalizeErrorMessage, translateError } from "@/utils/errors";

import { Webhook } from "../../schema/webhook";
import { RotateWebhookSecretDialog } from "./RotateWebhookSecretDialog";
import { WebhookSecretDialog, WebhookSecretDialogVariant } from "./WebhookSecretDialog";
import { WebhookTestEventDialog } from "./WebhookTestEventDialog";

type SigningWebhook = Pick<Webhook, "id" | "name" | "url">;

type OpenDialog =
	| { kind: "test"; webhook: SigningWebhook }
	| { kind: "rotate"; webhook: SigningWebhook }
	| {
			kind: "secret";
			variant: WebhookSecretDialogVariant;
			webhook: SigningWebhook;
			secret: string;
			previousSecretValidUntil: string | null;
	  };

/** What the Webhooks page can do with a webhook's signing (PREC-4249). */
export interface WebhookSigningActions {
	sendTestEvent: (webhook: SigningWebhook) => void;
	rotateSecret: (webhook: SigningWebhook) => void;
	revealSecret: (webhook: SigningWebhook) => Promise<void>;
	showCreatedSecret: (webhook: SigningWebhook, secret: string) => void;
}

/**
 * The signing dialogs of the Webhooks page in one place (PREC-4249): the row menu, the Edit dialog
 * and the Create dialog open them, and the page renders `dialogs` once, outside the table's loading
 * branch, so a refetch after a rotation cannot unmount a secret that is being shown.
 */
export function useWebhookSigningDialogs(): { actions: WebhookSigningActions; dialogs: ReactNode } {
	const { t } = useTranslation();
	const [open, setOpen] = useState<OpenDialog | null>(null);
	const [revealSecret] = useRevealWebhookSecretMutation();
	const close = useCallback(() => setOpen(null), []);

	const actions = useMemo<WebhookSigningActions>(
		() => ({
			sendTestEvent: (webhook) => setOpen({ kind: "test", webhook }),
			rotateSecret: (webhook) => setOpen({ kind: "rotate", webhook }),
			revealSecret: async (webhook) => {
				try {
					const { signingSecret } = await revealSecret(webhook.id).unwrap();

					setOpen({
						kind: "secret",
						variant: "revealed",
						webhook,
						secret: signingSecret,
						previousSecretValidUntil: null,
					});
				} catch (error) {
					const code = normalizeErrorMessage(
						(error as { data?: { message?: unknown } })?.data?.message
					);

					// Already shown once (409) names its way out; anything else is a plain failure.
					showGlobalToast(
						code === ERROR_TYPE.WEBHOOK_SECRET_ALREADY_REVEALED
							? translateError[code]
							: t("webhookSigning.reveal.failed")
					);
				}
			},
			showCreatedSecret: (webhook, secret) =>
				setOpen({
					kind: "secret",
					variant: "created",
					webhook,
					secret,
					previousSecretValidUntil: null,
				}),
		}),
		[revealSecret, t]
	);

	let dialogs: ReactNode = null;

	if (open?.kind === "test") {
		dialogs = (
			<WebhookTestEventDialog
				key={open.webhook.id}
				webhook={open.webhook}
				onClose={close}
				onRotate={() => setOpen({ kind: "rotate", webhook: open.webhook })}
			/>
		);
	} else if (open?.kind === "rotate") {
		dialogs = (
			<RotateWebhookSecretDialog
				webhookId={open.webhook.id}
				onCancel={close}
				onRotated={({ signingSecret, previousSecretValidUntil }) =>
					setOpen({
						kind: "secret",
						variant: "rotated",
						webhook: open.webhook,
						secret: signingSecret,
						previousSecretValidUntil,
					})
				}
			/>
		);
	} else if (open?.kind === "secret") {
		dialogs = (
			<WebhookSecretDialog
				previousSecretValidUntil={open.previousSecretValidUntil}
				secret={open.secret}
				variant={open.variant}
				webhookId={open.webhook.id}
				webhookName={open.webhook.name}
				onClose={close}
			/>
		);
	}

	return { actions, dialogs };
}
